import styled from "styled-components";
import { useState } from "react";
import { useFetchEntities } from "@/reactQueryHooks";
import moment from "moment";
import {
  SectionContainer,
  SectionTitle,
  EmptyMessage,
  colors,
} from "../styles";

//----------------------------------------------------

const RequestContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RequestCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  border-radius: 8px;
  background: ${colors.comment};
  transition: all 0.2s ease;
  border-left: 3px solid ${colors.primary};

  &:hover {
    background: ${colors.commentHover};
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  }
`;

const RequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const RequestMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RequestMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;

  i {
    font-size: 10px;
  }
`;

const RequestText = styled.span`
  font-size: 13px;
  color: ${colors.textPrimary};
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  opacity: 0.2;
  transition: opacity 0.2s ease;

  ${RequestCard}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => (props.delete ? colors.danger : colors.primary)};
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.delete ? "rgba(247, 37, 133, 0.1)" : "rgba(67, 97, 238, 0.1)"};
  }
`;

const RequestInputContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 5px;
`;

const RequestInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${colors.border};
  border-radius: 10px;
  outline: none;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15);
  }

  &::placeholder {
    color: ${colors.gray};
  }
`;

const SaveButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 46px;
  height: 46px;
  border-radius: 10px;
  border: none;
  background: ${(props) => (props.edit ? colors.info : colors.primary)};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.edit ? "#3B82F6" : colors.secondary)};
  }

  i {
    font-size: 16px;
  }
`;

const RequestsSection = ({ scheduleData, onSaveRequest, onDeleteRequest }) => {
  const [requestInput, setRequestInput] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const { data: entities } = useFetchEntities();

  const hasRequest = scheduleData?.request;

  // Get author information
  const requestAuthor = hasRequest
    ? entities?.data?.staff?.find(
        (user) => user._id === scheduleData.request?.author_id
      )
    : null;

  const requestDate = hasRequest
    ? moment(scheduleData.request?.date).format("DD/MM/YYYY HH:mm:ss")
    : null;

  const handleSaveRequest = () => {
    if (!requestInput.trim()) return;

    onSaveRequest(requestInput);
    setRequestInput("");
    setIsEditing(false);
  };

  const handleStartEditing = () => {
    setRequestInput(scheduleData?.request?.requestBody || "");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setRequestInput("");
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSaveRequest();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  return (
    <SectionContainer>
      <SectionTitle>
        <i className="fas fa-clipboard-check"></i>
        Requests
      </SectionTitle>

      {isEditing || !hasRequest ? (
        <RequestInputContainer>
          <RequestInput
            placeholder="Add a request..."
            value={requestInput}
            onChange={(e) => setRequestInput(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
          />
          <SaveButton onClick={handleSaveRequest} edit={isEditing}>
            <i className={`fa-solid ${isEditing ? "fa-check" : "fa-plus"}`}></i>
          </SaveButton>
          {isEditing && (
            <SaveButton
              onClick={handleCancelEdit}
              edit={false}
              style={{ background: colors.gray }}
            >
              <i className="fa-solid fa-times"></i>
            </SaveButton>
          )}
        </RequestInputContainer>
      ) : (
        <RequestContainer>
          <RequestCard>
            <RequestHeader>
              <RequestMeta>
                <RequestMetaItem>
                  <i className="fa-solid fa-user"></i>
                  <span>{requestAuthor?.name || "Unknown User"}</span>
                </RequestMetaItem>
                <RequestMetaItem>
                  <i className="fa-solid fa-clock"></i>
                  <span>{requestDate}</span>
                </RequestMetaItem>
              </RequestMeta>
              <ActionButtons>
                <ActionButton onClick={handleStartEditing}>
                  <i className="fa-solid fa-edit"></i>
                </ActionButton>
                <ActionButton
                  delete
                  onClick={() => {
                    if (window.confirm("Delete this request?")) {
                      onDeleteRequest();
                    }
                  }}
                >
                  <i className="fa-solid fa-trash"></i>
                </ActionButton>
              </ActionButtons>
            </RequestHeader>
            <RequestText>{scheduleData?.request?.requestBody}</RequestText>
          </RequestCard>
        </RequestContainer>
      )}

      {!hasRequest && !isEditing && (
        <EmptyMessage
          style={{
            background: "rgba(67, 97, 238, 0.05)",
            color: colors.primary,
          }}
        >
          No requests added
        </EmptyMessage>
      )}
    </SectionContainer>
  );
};

export default RequestsSection;
